// Reusable presentational building blocks that carry the notebook character.
// Keeping these in one place means the hand-drawn look is applied through
// composition rather than per-screen decoration.

import type { ReactNode } from 'react';
import {
  Alert,
  Anchor,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
  IconLock,
  IconPlugConnectedX,
} from '@tabler/icons-react';
import { useTranslation } from '@/i18n/I18nProvider';
import { ApiError } from '@/api/errors';
import { toneColor } from '@/theme/tones';
import { useAutoRetry } from '@/lib/useAutoRetry';

/** A panel with the drawer's hand-drawn outline. */
export function SketchCard({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): ReactNode {
  return (
    <Paper className={['sensing-sketch', className].filter(Boolean).join(' ')} p="lg">
      {children}
    </Paper>
  );
}

/** A page heading with the handwritten face and a marker underline. */
export function PageHeading({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  return (
    <Group justify="space-between" align="flex-start" wrap="wrap" gap="md" mb="md">
      <Stack gap={4}>
        <Title order={1} className="sensing-hand">
          <span className="sensing-underline">{title}</span>
        </Title>
        {description ? (
          <Text c="dimmed" size="sm" maw={640}>
            {description}
          </Text>
        ) : null}
      </Stack>
      {actions ? <Group gap="xs">{actions}</Group> : null}
    </Group>
  );
}

/** Centered loading state with a visible label for assistive technology. */
export function LoadingState({ label }: { label?: string }): ReactNode {
  const t = useTranslation();
  return (
    <Group justify="center" p="xl" gap="sm">
      <Loader size="sm" aria-hidden />
      <Text c="dimmed" role="status" aria-live="polite">
        {label ?? t('common.loading')}
      </Text>
    </Group>
  );
}

/** Empty state with an optional action. */
export function EmptyState({
  title,
  description,
  action,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
}): ReactNode {
  const t = useTranslation();
  return (
    <Stack align="center" gap="xs" p="xl">
      <IconInfoCircle size={28} aria-hidden />
      <Text fw={600}>{title ?? t('common.empty')}</Text>
      {description ? (
        <Text c="dimmed" size="sm" ta="center" maw={420}>
          {description}
        </Text>
      ) : null}
      {action ? <Group mt="xs">{action}</Group> : null}
    </Stack>
  );
}

/**
 * Render a typed API error as localized copy. With `onRetry`, a failure that
 * can heal is retried automatically (see useAutoRetry) and the copy says so;
 * there is no retry button. Raw server messages are never shown unless
 * `showDetail` asks for the server's own explanation.
 */
export function ErrorState({
  error,
  onRetry,
  title,
  showDetail = false,
}: {
  error: unknown;
  onRetry?: () => void;
  title?: string | undefined;
  /**
   * Also show the server's own explanation for a refused request. Use it where
   * the reason is actionable (a module that needs another mode, an untrusted
   * signature); the text is the server's and is not translated.
   */
  showDetail?: boolean;
}): ReactNode {
  const t = useTranslation();
  const apiError = error instanceof ApiError ? error : null;
  const code = apiError?.code;
  const healable = apiError ? apiError.isRetryable || code === 'aborted' : true;
  const retrying = onRetry !== undefined && healable;
  useAutoRetry(onRetry, retrying);
  const message =
    code && code !== 'network_error' && code !== 'invalid_response' && code !== 'aborted'
      ? t(`errors.${code}`)
      : t('errors.generic');
  const retryNote = retrying ? (
    <Group gap={6} wrap="nowrap">
      <Loader size={12} aria-hidden />
      <Text size="xs" c="dimmed">
        {t('common.retryingAutomatically')}
      </Text>
    </Group>
  ) : null;

  if (code === 'forbidden') {
    return (
      <Alert
        color="amber"
        variant="light"
        icon={<IconLock size={18} aria-hidden />}
        title={t('common.forbiddenTitle')}
      >
        <Text size="sm">{t('common.forbiddenBody')}</Text>
      </Alert>
    );
  }

  if (code === 'network_error' || code === 'aborted') {
    return (
      <Alert
        color="stone"
        variant="light"
        icon={<IconPlugConnectedX size={18} aria-hidden />}
        title={t('offline.title')}
      >
        <Stack gap="xs">
          <Text size="sm">{t('offline.unavailable')}</Text>
          {retryNote}
        </Stack>
      </Alert>
    );
  }

  return (
    <Alert
      color="rust"
      variant="light"
      icon={<IconAlertTriangle size={18} aria-hidden />}
      title={title ?? t('common.error')}
    >
      <Stack gap="xs">
        <Text size="sm">{message}</Text>
        {showDetail && apiError?.serverMessage ? (
          <Text size="sm" className="sensing-numeric" style={{ whiteSpace: 'normal' }}>
            {apiError.serverMessage}
          </Text>
        ) : null}
        {retryNote}
      </Stack>
    </Alert>
  );
}

/** Inline success notice. */
export function SuccessNotice({ message }: { message: string }): ReactNode {
  return (
    <Alert
      color="moss"
      variant="light"
      icon={<IconCircleCheck size={18} aria-hidden />}
      role="status"
    >
      <Text size="sm">{message}</Text>
    </Alert>
  );
}

/** A labelled definition row used in detail panels. */
export function DetailRow({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
      <Text c="dimmed" size="sm" component="span">
        {label}
      </Text>
      {/* Rendered as a div: the value may hold a Group, and a block element
          inside a paragraph is invalid HTML. */}
      <Text size="sm" ta="right" component="div" style={{ wordBreak: 'break-word' }}>
        {children}
      </Text>
    </Group>
  );
}

/** A titled section with the marker underline. The title is optional: a section
 * that already sits under a page heading of the same name would otherwise
 * repeat it. */
export function Section({
  title,
  actions,
  children,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <Stack gap="sm">
      {title === undefined && actions === undefined ? null : (
        <Group justify="space-between" align="center" wrap="wrap">
          {title === undefined ? null : (
            <Title order={3} className="sensing-hand">
              <span className="sensing-underline">{title}</span>
            </Title>
          )}
          {actions}
        </Group>
      )}
      {children}
    </Stack>
  );
}

/** An external link styled consistently. */
export function ExternalLink({ href, children }: { href: string; children: ReactNode }): ReactNode {
  return (
    <Anchor href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </Anchor>
  );
}

/** A small icon with an accessible description for status rows. */
export function StatusIcon({
  tone,
  label,
}: {
  tone: 'ok' | 'warn' | 'info';
  label: string;
}): ReactNode {
  const color = toneColor(tone === 'ok' ? 'ok' : tone === 'warn' ? 'warn' : 'info');
  const Icon =
    tone === 'ok' ? IconCircleCheck : tone === 'warn' ? IconAlertTriangle : IconInfoCircle;
  return (
    <ThemeIcon color={color} variant="light" size="sm" aria-label={label} title={label}>
      <Icon size={14} aria-hidden />
    </ThemeIcon>
  );
}

/** A subtle separator carrying the notebook margin line. */
export function MutedDivider(): ReactNode {
  return <Divider my="sm" color="var(--sensing-border)" />;
}
